/**
 * Derives the display values for a watchlist badge (first name + extra count).
 *
 * Used by both the web table (DataCellView) and mobile card
 * (MobileTargetCardContainer) to avoid duplicating the derivation logic.
 * @module features/scouting/utils/deriveWatchlistDisplay
 */

/** Display values for a watchlist badge */
interface WatchlistDisplay {
  /** First watchlist name to show */
  readonly firstName: string;
  /** Number of additional watchlists beyond the first */
  readonly extraCount: number;
}

/**
 * Derives firstName and extraCount from a watchlist entries array.
 * @param entries - Watchlist entries with id and name
 * @returns Display values for the badge
 */
export function deriveWatchlistDisplay(
  entries: readonly { readonly id: string; readonly name: string }[]
): WatchlistDisplay {
  return {
    firstName: entries[0]?.name ?? "",
    extraCount: Math.max(0, entries.length - 1),
  };
}

/**
 * Derives firstName and extraCount from a comma-joined display string
 * and a total count. Used when only the pre-formatted string is available.
 * @param displayValue - Comma-joined watchlist names (e.g. "Test3, Test2")
 * @param watchlistCount - Total number of watchlists
 * @returns Display values for the badge
 */
export function deriveWatchlistDisplayFromString(
  displayValue: string,
  watchlistCount: number
): WatchlistDisplay {
  const names = displayValue.split(", ");
  return {
    firstName: names[0],
    extraCount: watchlistCount > 1 ? watchlistCount - 1 : 0,
  };
}
